// Pure helpers for the APPLY_IMAGE_URL_MAP action — the FE mirror of the
// backend's applyLogoUrlMap. After "apply logo to all training images", the job
// returns a urlMap { oldUrl: newLogoedUrl } for every stamped image; these swap
// those URLs across the editor's in-memory state so the canvas shows the new
// logoed images. Only image URLs change — everything else stays byte-for-byte.
//
// Kept dependency-free (plain objects in/out) so they're unit-testable in
// isolation, and used by both plugin_toolbars_by_id and view_toolbars_by_id
// reducers. Returns the SAME reference when nothing matched, so an unrelated
// urlMap never forces a re-render.

/**
 * Swap pluginToolbarsById[*].state.(url|src) using the urlMap.
 * @param {object} state pluginToolbarsById slice
 * @param {Record<string,string>} urlMap oldUrl → newUrl
 */
export function applyUrlMapToPluginToolbars(state, urlMap) {
  if (!state || !urlMap || Object.keys(urlMap).length === 0) return state;

  let changed = false;
  const next = {};
  for (const [id, box] of Object.entries(state)) {
    const st = box && box.state;
    const oldUrl = st && st.url;
    const oldSrc = st && st.src;
    const hasUrl = typeof oldUrl === "string" && urlMap[oldUrl];
    const hasSrc = typeof oldSrc === "string" && urlMap[oldSrc];
    if (hasUrl || hasSrc) {
      changed = true;
      next[id] = {
        ...box,
        state: {
          ...st,
          ...(hasUrl ? { url: urlMap[oldUrl] } : {}),
          ...(hasSrc ? { src: urlMap[oldSrc] } : {}),
        },
      };
    } else {
      next[id] = box;
    }
  }
  return changed ? next : state;
}

/**
 * Swap viewToolbarsById[*].background using the urlMap.
 * @param {object} state viewToolbarsById slice
 * @param {Record<string,string>} urlMap oldUrl → newUrl
 */
export function applyUrlMapToViewToolbars(state, urlMap) {
  if (!state || !urlMap || Object.keys(urlMap).length === 0) return state;

  let changed = false;
  const next = {};
  for (const [id, view] of Object.entries(state)) {
    const bg = view && view.background;
    if (typeof bg === "string" && urlMap[bg]) {
      changed = true;
      next[id] = { ...view, background: urlMap[bg] };
    } else {
      next[id] = view;
    }
  }
  return changed ? next : state;
}
